import { useEffect, useState, useRef } from 'react'
import { convertSearchSpringCarouselProductPricingToMarketPrice, getCarouselItems, isSearchSpringCarouselProductResponse, debounce, elInViewPort } from '../../helpers/searchSpringHelpers'
import { SearchSpringCarouselResponse, SSCarouselResult, Payload } from '../../search-spring/types/types'
import ProductCarousel from '../../product-carousel/components/ProductCarousel'
import { generateBeacon, generateBeaconProducts, postBeaconTracking } from '../../helpers/searchspring-tracking'
import { getCookie } from '../../helpers/cookies'

declare global {
  interface Window {
    searchSpringCarouselHeading: string
  }
}

declare var Shopify: { currency: { active: string } }
type HandleBeacon = {
  sku?: string
  profile?: string
  products: SSCarouselResult[]
}

const carouselDataset = document.getElementById('ss-carousel-target')?.dataset
const carouselTag = carouselDataset?.carouselId
const carouselProductId = carouselDataset?.productId
const carouselHeading = window?.searchSpringCarouselHeading || 'Recommended for you'
const sku: string = carouselDataset?.productSeed ?? ''
const profile: string = carouselDataset?.carouselId ?? ''

const handleBeaconProfile = ({ sku, profile, products }: HandleBeacon) => {
  const trackingSku = sku || ''
  const trackingProfile = profile || ''
  const arrs = [generateBeacon(trackingSku, trackingProfile, 'profile.render'), ...generateBeaconProducts(products, trackingProfile, 'profile.product.render', null)]

  postBeaconTracking(arrs)
}

const handleBeaconProfileImpression = ({ sku, profile, products }: HandleBeacon) => {
  const trackingSku = sku || ''
  const trackingProfile = profile || ''
  const arrs = [generateBeacon(trackingSku, trackingProfile, 'profile.impression'), ...generateBeaconProducts(products, trackingProfile, 'profile.product.impression', null)]

  postBeaconTracking(arrs)
}

const CarouselInit = () => {
  const [products, setProducts] = useState<SSCarouselResult[]>([])
  const [error, setError] = useState(false)
  const [isFetchingFalse, setIsFetchingFalse] = useState(false)
  const elRef = useRef(null)

  const getProductData = async () => {
    const ssViewedProducts = getCookie('ssViewedProducts')
    const ssShopperId = getCookie('ssShopperId')
    const ssCartProducts = getCookie('ssCartProducts')

    const payload = {
      limits: '8',
      tags: carouselTag
    } as Payload

    if (carouselProductId !== '') {
      payload.productId = carouselProductId
    }

    if (ssViewedProducts) {
      payload.lastViewed = ssViewedProducts
    }

    if (ssShopperId) {
      payload.shopper = ssShopperId
    }

    if (ssCartProducts) {
      payload.cart = ssCartProducts
    }

    const response = await getCarouselItems(payload)
    if (isSearchSpringCarouselProductResponse(response)) {
      if (Shopify.currency.active != "AUD") {
        const pricedResponse: SearchSpringCarouselResponse = await convertSearchSpringCarouselProductPricingToMarketPrice(response[0])
        setProducts(pricedResponse.results || response[0].results)
        handleBeaconProfile({ sku, profile, products: pricedResponse.results || response[0].results })
        const items = pricedResponse.results || response[0].results
        initTracking(items)
      } else {
        setProducts(response[0].results)
        handleBeaconProfile({ sku, profile, products: response[0].results })
        const items = response[0].results
        initTracking(items)
      }
    } else {
      setError(true)
      setIsFetchingFalse(true)
    }
  }

  const handleTrackingImpression = (items: SSCarouselResult[]) => {
    if (isFetchingFalse) {
      window.removeEventListener('scroll', onTrackingImpression)

      return
    }
    if (elInViewPort(elRef.current, false)) {
      handleBeaconProfileImpression({ sku, profile, products: items })
      window.removeEventListener('scroll', onTrackingImpression)
    }
  }

  const onTrackingImpression = (items: SSCarouselResult[] | any) => debounce(() => handleTrackingImpression(items), 500)

  const initTracking = (items: SSCarouselResult[]) => {
    window.addEventListener('scroll', onTrackingImpression(items))
  }

  useEffect(() => {
    getProductData()
  }, [])

  if (error) {
    return <div className="text-xl font-bstSpyre mt-4 text-center mx-5">Sorry, There was an error creating our carousels, please try again later.</div>
  }

  return (
    <>
      {products.length > 0 && (
        <div className="py-10 w-full mx-auto md:px-5 md:py-2XL md:flex md:flex-col" ref={elRef}>
          <h2 className="font-bstSpyre text-2xl uppercase text-center mb-LG md:mb-DLG">{carouselHeading}</h2>
          <ProductCarousel
            classSlider="md-max:w-[230px] md:min-h-[500px]"
            classWrap="flex"
            isCardNative={false}
            products={products}
            spaceBetween={0}
            slidesPerView="auto"
            profile={profile}
            sku={sku}
            breakpoints={{
              768: { slidesPerView: 3, spaceBetween: 5 },
              1024: { slidesPerView: 4, spaceBetween: 5 }
            }}
          />
        </div>
      )}
    </>
  )
}

export default CarouselInit
